import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { AnnotationEditor } from "@markerjs/markerjs-ui";
import { getPresignedImageUrlForFile, uploadFile } from "@/network/network.jsx";
import { createFile, updateFileById } from "@/react/components/entityViews/files/api/files.service";
import { useEntityFileContext } from "@/react/components/entityViews/files/context/EntityFileContext";
import { toast } from "sonner";
import type { SwivuFile } from "@/react/components/entityViews/files/types/swivuFile";
import type { EntityRecord } from "@/types/entity";
import { cn } from "@/modern/utils";

interface UploadResponse {
  id: string | number;
  fileName: string;
}

interface CleanupRef {
  containerElement: HTMLDivElement | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  saveHandler: ((event: any) => Promise<void>) | null;
  closeHandler: (() => void) | null;
  resizeObserver: ResizeObserver | null;
  themeObserver: MutationObserver | null;
}

interface ImageAnnotationEditorProps {
  entityRecord: EntityRecord;
  fileToAnnotate: SwivuFile;
  onClose: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onAfterImageAnnotate?: (fileToAnnotate: SwivuFile, annotatedImageRecord: any) => void;
  className?: string;
}

export interface ImageAnnotationEditorHandle {
  requestClose: () => void;
}

/**
 * MarkerJS only supports light/dark chrome. Swivu maps all four themes through
 * colorScheme: light → light; dark, cobalt, and nature → dark.
 */
const MARKERJS_UI_THEME_FIX_ID = "swivu-markerjs-theme-fix";

/**
 * MarkerJS UI ships a prefers-color-scheme:dark rule on #mainContainer that beats
 * [data-theme=light] when the OS is in dark mode. Re-assert light tokens with
 * higher specificity so Swivu light theme stays light regardless of OS preference.
 */
const MARKERJS_LIGHT_OS_OVERRIDE = `@media (prefers-color-scheme: dark) {
  #mainContainer[data-theme="light"] {
    color-scheme: light;
    --color-base-100: oklch(100% 0 0);
    --color-base-200: oklch(98% 0 0);
    --color-base-300: oklch(95% 0 0);
    --color-base-content: oklch(21% 0.006 285.885);
    --color-primary: oklch(45% 0.24 277.023);
    --color-primary-content: oklch(93% 0.034 272.788);
    --color-secondary: oklch(65% 0.241 354.308);
    --color-secondary-content: oklch(94% 0.028 342.258);
    --color-accent: oklch(77% 0.152 181.912);
    --color-accent-content: oklch(38% 0.063 188.416);
    --color-neutral: oklch(14% 0.005 285.823);
    --color-neutral-content: oklch(92% 0.004 286.32);
    --color-info: oklch(74% 0.16 232.661);
    --color-info-content: oklch(29% 0.066 243.157);
    --color-success: oklch(76% 0.177 163.223);
    --color-success-content: oklch(37% 0.077 168.94);
    --color-warning: oklch(82% 0.189 84.429);
    --color-warning-content: oklch(41% 0.112 45.904);
    --color-error: oklch(71% 0.194 13.428);
    --color-error-content: oklch(27% 0.105 12.094);
    --radius-selector: 0.5rem;
    --radius-field: 0.25rem;
    --radius-box: 0.5rem;
    --size-selector: 0.25rem;
    --size-field: 0.25rem;
    --border: 1px;
    --depth: 1;
    --noise: 0;
  }
}`;

const resolveAnnotationEditorTheme = (): "light" | "dark" => {
  const scheme = document.documentElement.dataset.theme;
  return scheme === "dark" ? "dark" : "light";
};

const applyAnnotationEditorTheme = (markerEditor: AnnotationEditor) => {
  const theme = resolveAnnotationEditorTheme();
  markerEditor.theme = theme;

  const shadowRoot = markerEditor.shadowRoot;
  if (!shadowRoot) {
    return;
  }

  let fixStyle = shadowRoot.getElementById(MARKERJS_UI_THEME_FIX_ID) as HTMLStyleElement | null;
  if (!fixStyle) {
    fixStyle = document.createElement("style");
    fixStyle.id = MARKERJS_UI_THEME_FIX_ID;
    shadowRoot.appendChild(fixStyle);
  }

  fixStyle.textContent = theme === "light" ? MARKERJS_LIGHT_OS_OVERRIDE : "";
};

/** Editor shell layout — do not style the marker-area image; MarkerJS owns zoom via zoomLevel. */
const applyEditorLayout = (host: HTMLElement) => {
  try {
    const wrapper = host.querySelector(".swivu-annotation-editor") as HTMLElement | null;
    const annotationEditor = host.querySelector("mjsui-annotation-editor") as HTMLElement | null;
    if (!wrapper || !annotationEditor) return;

    wrapper.style.display = "flex";
    wrapper.style.flexDirection = "column";
    wrapper.style.width = "100%";
    wrapper.style.height = "100%";
    wrapper.style.minHeight = "0";

    annotationEditor.style.display = "flex";
    annotationEditor.style.flexDirection = "column";
    annotationEditor.style.width = "100%";
    annotationEditor.style.height = "100%";
    annotationEditor.style.minHeight = "0";
  } catch {
    // Silently fail - non-critical styling adjustment
  }
};

const getMarkerCanvasContainer = (markerArea: AnnotationEditor["markerArea"]): HTMLElement | null =>
  markerArea.shadowRoot?.querySelector(".canvas-container") as HTMLElement | null;

/**
 * Fit zoom = largest scale where the full image is still visible (object-contain boundary).
 * Uses MarkerJS's canvas container — same element autoZoom() measures internally.
 */
const applyMaxContainZoom = (markerEditor: AnnotationEditor): boolean => {
  const { markerArea } = markerEditor;
  if (markerArea.targetWidth <= 0 || markerArea.targetHeight <= 0) {
    return false;
  }

  const canvas = getMarkerCanvasContainer(markerArea);
  if (!canvas) {
    return false;
  }

  const canvasWidth = canvas.clientWidth;
  const canvasHeight = canvas.clientHeight;
  if (canvasWidth <= 0 || canvasHeight <= 0) {
    return false;
  }

  const fitScale = Math.min(
    canvasWidth / markerArea.targetWidth,
    canvasHeight / markerArea.targetHeight,
  );

  markerArea.autoZoomIn = true;
  markerArea.autoZoomOut = true;
  markerArea.zoomLevel = fitScale;
  return true;
};

const scheduleMaxContainZoom = (markerEditor: AnnotationEditor) => {
  let lastWidth = 0;
  let lastHeight = 0;
  let stableFrames = 0;

  const tryFit = (attempt = 0) => {
    const canvas = getMarkerCanvasContainer(markerEditor.markerArea);
    const width = canvas?.clientWidth ?? 0;
    const height = canvas?.clientHeight ?? 0;

    if (width > 0 && height > 0) {
      if (width === lastWidth && height === lastHeight) {
        stableFrames += 1;
      } else {
        stableFrames = 0;
      }
      lastWidth = width;
      lastHeight = height;
    }

    if (stableFrames >= 2 && applyMaxContainZoom(markerEditor)) {
      return;
    }

    if (attempt < 30) {
      requestAnimationFrame(() => tryFit(attempt + 1));
    }
  };

  tryFit();
  window.setTimeout(() => applyMaxContainZoom(markerEditor), 650);
};

/**
 * Uploads the annotated image file
 */
const uploadAnnotatedFile = async (dataUrl: string, fileToAnnotate: SwivuFile): Promise<UploadResponse> => {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  const file = new File([blob], fileToAnnotate.name, { type: blob.type });
  const { data: uploadData } = await uploadFile(file, file.name);
  return uploadData as UploadResponse;
};

/**
 * ImageAnnotationEditor Component
 * Provides an interface for annotating images using MarkerJS
 */
const ImageAnnotationEditor = forwardRef<ImageAnnotationEditorHandle, ImageAnnotationEditorProps>(
  ({ entityRecord, fileToAnnotate, onClose, onAfterImageAnnotate, className }, ref) => {
  const { files } = useEntityFileContext();
  const filesRef = useRef(files);
  filesRef.current = files;

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const onAfterImageAnnotateRef = useRef(onAfterImageAnnotate);
  onAfterImageAnnotateRef.current = onAfterImageAnnotate;

  const hostRef = useRef<HTMLDivElement>(null);
  const isInitRef = useRef(false);
  const isSavingRef = useRef(false);
  const cleanupRef = useRef<CleanupRef>({
    containerElement: null,
    saveHandler: null,
    closeHandler: null,
    resizeObserver: null,
    themeObserver: null,
  });

  useImperativeHandle(ref, () => ({
    requestClose: () => {
      if (cleanupRef.current.closeHandler) {
        cleanupRef.current.closeHandler();
        return;
      }
      onCloseRef.current?.();
    },
  }));

  useEffect(() => {
    // Prevent double initialization across StrictMode / callback identity changes
    if (isInitRef.current) {
      return;
    }
    isInitRef.current = true;

    // Presigned URL is fetched below — list `url` is not required
    if (!fileToAnnotate?.id || !fileToAnnotate?.name) {
      toast.error("Unable to initialize editor. Missing file data.");
      onCloseRef.current?.();
      isInitRef.current = false;
      return;
    }

    const isHasChanges = (markerEditor: AnnotationEditor) => {
      return markerEditor.markerArea.getState().markers.length > 0;
    };

    /**
     * Cleans up all resources used by the editor
     */
    const cleanupEditor = () => {
      if (cleanupRef.current.containerElement) {
        cleanupRef.current.containerElement.remove();
        cleanupRef.current.containerElement = null;
      }

      cleanupRef.current.saveHandler = null;
      cleanupRef.current.closeHandler = null;
      cleanupRef.current.resizeObserver?.disconnect();
      cleanupRef.current.resizeObserver = null;
      cleanupRef.current.themeObserver?.disconnect();
      cleanupRef.current.themeObserver = null;
      isInitRef.current = false;
    };

    /**
     * Initializes and displays the annotation editor
     */
    const initializeEditor = async () => {
      try {
        const host = hostRef.current;
        if (!host) {
          throw new Error("Annotation stage host not found");
        }

        const containerElement = document.createElement("div");
        containerElement.classList.add("swivu-annotation-editor");
        host.appendChild(containerElement);
        cleanupRef.current.containerElement = containerElement;

        const presignedResponse = await getPresignedImageUrlForFile(fileToAnnotate.id);
        const presignedImageUrl = presignedResponse.data.url;

        const targetImg = document.createElement("img");
        targetImg.crossOrigin = "anonymous";
        targetImg.src = presignedImageUrl;

        const markerEditor = new AnnotationEditor();
        markerEditor.targetImage = targetImg;
        containerElement.appendChild(markerEditor);
        applyAnnotationEditorTheme(markerEditor);

        const themeObserver = new MutationObserver(() => {
          applyAnnotationEditorTheme(markerEditor);
        });
        themeObserver.observe(document.documentElement, {
          attributes: true,
          attributeFilter: ["data-theme"],
        });
        cleanupRef.current.themeObserver = themeObserver;

        applyEditorLayout(host);
        const runInitialFit = () => {
          applyEditorLayout(host);
          scheduleMaxContainZoom(markerEditor);
        };
        targetImg.addEventListener("load", runInitialFit, { once: true });
        if (targetImg.complete) {
          runInitialFit();
        }

        const resizeObserver = new ResizeObserver(() => {
          applyEditorLayout(host);
        });
        resizeObserver.observe(host);
        cleanupRef.current.resizeObserver = resizeObserver;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const handleSave = async (event: any) => {
          if (isSavingRef.current) {
            return;
          }

          try {
            if (!isHasChanges(markerEditor)) {
              cleanupEditor();
              onCloseRef.current?.();
              return;
            }

            isSavingRef.current = true;
            toast.loading("Uploading annotated image...", { id: "file-upload" });

            const dataUrl = event.detail?.dataUrl as string | undefined;
            if (!dataUrl) {
              throw new Error("No image data received from editor");
            }

            const uploadData = await uploadAnnotatedFile(dataUrl, fileToAnnotate);

            if (!uploadData?.id) {
              throw new Error("Invalid upload response");
            }

            if (fileToAnnotate.document == null) {
              throw new Error("Cannot annotate a file with no document");
            }

            const maxOrder = filesRef.current.reduce(
              (max: number, f: SwivuFile) => Math.max(max, f.order || 0),
              0,
            );

            const entityType =
              fileToAnnotate.task != null
                ? "task"
                : fileToAnnotate.resource != null
                  ? "resource"
                  : fileToAnnotate.client != null
                    ? "client"
                    : fileToAnnotate.location != null
                      ? "location"
                      : null;
            const entityId =
              fileToAnnotate.task ?? fileToAnnotate.resource ?? fileToAnnotate.client ?? fileToAnnotate.location;

            const newRecord = await createFile({
              document: Number(fileToAnnotate.document),
              board_id: fileToAnnotate.board,
              ...(entityType && entityId != null ? { [entityType]: entityId } : {}),
              order: maxOrder + 1,
            });

            await updateFileById(fileToAnnotate.id, { document: Number(uploadData.id), is_annotated: true });

            toast.success("Successfully uploaded annotated image", { id: "file-upload" });
            onAfterImageAnnotateRef.current?.(fileToAnnotate, newRecord);

            cleanupEditor();
            onCloseRef.current?.();
          } catch (error) {
            console.error("File upload error:", error);
            toast.error((error as Error).message || "Failed to upload. Please try again.", { id: "file-upload" });
          } finally {
            isSavingRef.current = false;
          }
        };

        const handleClose = () => {
          const confirmed = isHasChanges(markerEditor)
            ? window.confirm("Are you sure you want to close the editor? Any unsaved changes will be lost.")
            : true;

          if (confirmed) {
            cleanupEditor();
            onCloseRef.current?.();
          }
        };

        cleanupRef.current.saveHandler = handleSave;
        cleanupRef.current.closeHandler = handleClose;

        markerEditor.addEventListener("editorsave", handleSave);
        markerEditor.addEventListener("editorclose", handleClose);
      } catch (error) {
        console.error("Error initializing annotation editor:", error);
        toast.error((error as Error).message || "Failed to initialize editor. Please try again.");
        cleanupEditor();
        onCloseRef.current?.();
      }
    };

    void initializeEditor();

    // Only cleanup on unmount or file identity change — not when parent callbacks change
    // (callbacks stay current via refs; intentional narrow deps)
    return () => {
      cleanupEditor();
    };
  }, [fileToAnnotate.id, fileToAnnotate.name, fileToAnnotate.document, entityRecord.id]);

  return (
    <div
      ref={hostRef}
      className={cn("swivu-annotation-editor-top-container", className)}
      data-annotation-stage-host
    />
  );
});

ImageAnnotationEditor.displayName = "ImageAnnotationEditor";

export default ImageAnnotationEditor;
